const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
const HAS_AI_KEY = true;

function getToken() {
  try {
    return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token || null;
  } catch { return null; }
}

async function serverAI(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'AI request failed');
  }
  const data = await res.json();
  return data.text;
}

// Build a rich system prompt from the worker's full job context
function buildSystemPrompt(context) {
  const { cards, workerName, boardName, today, conversations, checklists } = context;

  const jobDetails = cards.map(c => {
    const checklistSummary = (c.checklists || []).map(cl => {
      const done  = cl.checkItems?.filter(i => i.state === 'complete').length || 0;
      const total = cl.checkItems?.length || 0;
      const items = cl.checkItems?.map(i =>
        `    [${i.state === 'complete' ? '✓' : ' '}] ${i.name}`
      ).join('\n') || '';
      return `  Checklist: ${cl.name} (${done}/${total} complete)\n${items}`;
    }).join('\n');

    const commentHistory = (c.actions || []).slice(0, 5).map(a =>
      `  Note (${new Date(a.date).toLocaleDateString()}): ${a.data?.text || ''}`
    ).join('\n');

    const crew = (c.members || []).map(m => m.fullName).join(', ') || 'Unassigned';
    const labels = (c.labels || []).filter(l => l.name).map(l => l.name).join(', ') || 'None';

    return `
JOB: ${c.name}
  Status: ${c.listName}
  Due: ${c.due ? new Date(c.due).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'No due date'}
  Labels/Tags: ${labels}
  Crew assigned: ${crew}
  Description: ${c.desc || 'No description provided'}
${checklistSummary}
${commentHistory ? 'Recent notes:\n' + commentHistory : ''}
`;
  }).join('\n---\n');

  // Build conversation threads section
  // conversations: [{projectName, subJobName, notes: [{note, created_at, first_name, last_name}]}]
  let conversationSection = '';
  if (conversations && conversations.length > 0) {
    const threadLines = [];
    for (const thread of conversations) {
      const { projectName, subJobName, notes = [] } = thread;
      if (!notes.length) continue;
      threadLines.push(`\nProject: ${projectName} → ${subJobName}`);
      for (const n of notes.slice(0, 5)) {
        const date = n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
        const author = [n.first_name, n.last_name].filter(Boolean).join(' ');
        threadLines.push(`  [${date}${author ? ' · ' + author : ''}] ${(n.note || '').slice(0, 300)}`);
      }
    }
    if (threadLines.length > 0) {
      conversationSection = `\nRECENT DAILY NOTES FROM YOUR PROJECTS:\n${threadLines.join('\n')}\n`;
    }
  }

  let checklistSection = '';
  if (checklists && checklists.length > 0) {
    const lines = checklists.map(c => {
      const cat = c.category ? ` [${c.category}]` : '';
      const kw = c.keywords ? ` (also called: ${c.keywords})` : '';
      const steps = (c.items || []).map((it, i) => `    ${i + 1}. ${it}`).join('\n');
      return `- ${c.title}${cat}${kw}\n${steps}`;
    }).join('\n');
    checklistSection = `\nCOMPANY CHECKLISTS — Titan's own step-by-step lists. When a worker asks for a checklist, the steps, or how to do a task and one of these matches by title or keywords, give them THAT list's steps:\n${lines}\n`;
  }

  return `You are a smart, concise field assistant for ${workerName}, who works for Titan Electrical Contracting in Reno, Nevada.

Today's date is ${today}.
Board: ${boardName}

${cards.length === 0
  ? `${workerName} has no jobs currently assigned to them.`
  : `${workerName} has ${cards.length} job(s) assigned:\n${jobDetails}`
}
${conversationSection}${checklistSection}
YOUR ROLE:
- Answer questions about jobs, materials, locations, checklists, crew, and schedules
- CHECKLISTS: if a worker asks for a checklist or the steps to do a task, look in COMPANY CHECKLISTS above. If one matches (by title or its "also called" keywords), give those exact steps as a clean numbered list and name the checklist. If none matches, offer a sensible general checklist but say it isn't an official Titan checklist yet.
- When asked "what do I need" or "what's on my jobs", give a clear practical summary — location, what needs doing, what's left on checklists, any notes from the team
- If asked about materials or equipment, pull from the job description and checklist items
- Keep answers short and scannable — this worker is on their phone at a job site
- Use bullet points for lists of items
- If you don't have specific info (like a materials list that wasn't added to the card), say so and suggest they check with Ryan
- Refer to the worker by their first name occasionally to keep it personal
- Today is ${today} — use this to figure out what's due soon or upcoming`;
}

// Mock AI responses for demo without Anthropic key
function mockResponse(message, cards, workerName) {
  const msg = message.toLowerCase();
  const firstName = workerName.split(' ')[0];

  const activeJobs = cards.filter(c =>
    c.listName?.toLowerCase().includes('progress') || c.listName?.toLowerCase().includes('active')
  );
  const upcomingJobs = cards.filter(c =>
    c.listName?.toLowerCase().includes('backlog') || c.listName?.toLowerCase().includes('scheduled')
  );

  if (msg.includes('tomorrow') || msg.includes('need') || msg.includes('jobs')) {
    if (cards.length === 0) return `Hey ${firstName}, you don't have any jobs assigned right now. Check with Ryan on Trello.`;

    const lines = [`Here's what you've got, ${firstName}:`];
    cards.forEach(card => {
      const due = card.due ? new Date(card.due).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No date';
      const openItems = card.checklists?.flatMap(cl =>
        cl.checkItems?.filter(i => i.state === 'incomplete').map(i => i.name) || []
      ) || [];
      lines.push(`\n📍 **${card.name}**`);
      lines.push(`Status: ${card.listName} · Due ${due}`);
      if (card.desc) {
        // Pull first line of desc as location/summary
        const firstLine = card.desc.split('\n')[0];
        lines.push(`${firstLine}`);
      }
      if (openItems.length > 0) {
        lines.push(`Still to do:`);
        openItems.slice(0, 4).forEach(item => lines.push(`• ${item}`));
        if (openItems.length > 4) lines.push(`• …and ${openItems.length - 4} more`);
      }
    });
    return lines.join('\n');
  }

  if (msg.includes('material') || msg.includes('equipment') || msg.includes('supply')) {
    if (cards.length === 0) return `No jobs assigned yet — nothing to pull materials for.`;
    const lines = [`Materials/equipment across your ${cards.length} job(s), ${firstName}:`];
    cards.forEach(card => {
      lines.push(`\n**${card.name}**`);
      const allItems = card.checklists?.flatMap(cl => cl.checkItems?.map(i => i.name) || []) || [];
      if (allItems.length > 0) {
        allItems.slice(0, 5).forEach(item => lines.push(`• ${item}`));
      } else if (card.desc) {
        lines.push(card.desc.split('\n')[0]);
      } else {
        lines.push('• Check card description — no checklist items yet');
      }
    });
    lines.push(`\nFor a full materials list, check with Ryan or the card attachments.`);
    return lines.join('\n');
  }

  if (msg.includes('status') || msg.includes('progress') || msg.includes('how') || msg.includes('where')) {
    const lines = [`Here's your job status, ${firstName}:`];
    cards.forEach(card => {
      const total = card.checklists?.reduce((a, cl) => a + (cl.checkItems?.length || 0), 0) || 0;
      const done  = card.checklists?.reduce((a, cl) => a + (cl.checkItems?.filter(i => i.state === 'complete').length || 0), 0) || 0;
      const pct = total > 0 ? Math.round((done / total) * 100) : null;
      lines.push(`• **${card.name}** — ${card.listName}${pct !== null ? ` (${pct}% complete)` : ''}`);
    });
    return lines.join('\n');
  }

  if (msg.includes('crew') || msg.includes('who') || msg.includes('team')) {
    const lines = [`Crew on your jobs, ${firstName}:`];
    cards.forEach(card => {
      const crew = (card.members || []).map(m => m.fullName).join(', ') || 'No crew assigned';
      lines.push(`• **${card.name}**: ${crew}`);
    });
    return lines.join('\n');
  }

  if (msg.includes('note') || msg.includes('update') || msg.includes('comment')) {
    const lines = [`Latest notes on your jobs:`];
    let found = false;
    cards.forEach(card => {
      if (card.actions?.length > 0) {
        found = true;
        const latest = card.actions[0];
        lines.push(`\n**${card.name}**`);
        lines.push(`${latest.data?.text || ''}`);
        lines.push(`— ${latest.memberCreator?.fullName}, ${new Date(latest.date).toLocaleDateString()}`);
      }
    });
    if (!found) lines.push('No notes yet on your assigned jobs.');
    return lines.join('\n');
  }

  // Default
  return `Hey ${firstName}! You've got ${cards.length} job(s) assigned right now. Ask me about your jobs, what you need, crew, status, or notes — I'll pull it from your cards.`;
}

export async function askAssistant(message, context, conversationHistory = []) {
  const { cards, workerName, boardName, conversations } = context;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const systemPrompt = buildSystemPrompt({ cards, workerName, boardName, today, conversations });

  const messages = [
    ...conversationHistory.map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text })),
    { role: 'user', content: message },
  ];
  // The Anthropic Messages API requires the first turn to be from the user.
  // The on-screen greeting is an assistant turn, so drop any leading
  // assistant messages before sending (otherwise the very first question 400s).
  while (messages.length && messages[0].role !== 'user') messages.shift();

  return serverAI('/ai/ask', { systemPrompt, messages });
}

// Generate a plain-English status summary for a sub-job given its tasks.
// Used in the admin project detail view.
export async function summarizeSubJob(projectName, subJobName, tasks) {
  const taskLines = tasks.map(t => {
    const parts = [`- ${t.title}`];
    if (t.trello_list_name) parts.push(`(${t.trello_list_name})`);
    if (t.status === 'complete') parts.push('[COMPLETE]');
    if (t.description) parts.push(`— ${t.description.slice(0, 120)}`);
    return parts.join(' ');
  }).join('\n');

  const prompt = `You are summarizing the status of a construction sub-job for Titan Electric, an electrical contractor in Reno, Nevada.

Project: ${projectName}
Sub-job: ${subJobName}
Tasks (${tasks.length} total):
${taskLines}

Write 2-3 sentences a project manager would find useful. Focus on:
- What is currently active or in progress
- Any notable items (blocked, overdue, or recently completed milestones)
- Overall momentum or next step

Be concise and direct. No bullet points — prose only. Do not open with task counts ("Of the X tasks…", "X of Y tasks…") — skip straight to what's happening. Do not repeat the sub-job name or project name at the start.`;

  return serverAI('/ai/summarize', { prompt });
}

export { HAS_AI_KEY };
